import { useState } from "react";
import { Mail } from "lucide-react";
import {
  AlertTriggers,
  defaultProfile,
  emailFromIdToken,
  NovaSynthProfile,
  persistProfile,
} from "@/lib/profile";
import { SlackConnect } from "./SlackConnect";

interface SettingsProps {
  idToken: string;
  profile: NovaSynthProfile | null;
  onClose: () => void;
  onSaved: (profile: NovaSynthProfile) => void;
}

const INDUSTRIES = [
  "Technology & Software", "Financial Services & Banking", "Healthcare & Life Sciences",
  "Retail & eCommerce", "Manufacturing & Industrial", "Professional Services & Consulting",
  "Education & EdTech", "Cybersecurity", "Media & Entertainment", "Telecommunications",
  "Logistics & Supply Chain", "Real Estate & PropTech", "Energy & Utilities", "Insurance",
  "Legal & LegalTech", "Automotive", "Travel & Hospitality", "Government & Public Sector",
  "Food & Beverage", "Non-profit & NGO",
];
const SIZES = ["1-50", "51-200", "201-1,000", "1,000+"];

const TRIGGERS: { key: keyof AlertTriggers; label: string }[] = [
  { key: "highThreat", label: "High threat detected (score ≥ 7)" },
  { key: "newProduct", label: "New product / feature launched" },
  { key: "pricingChange", label: "Pricing change detected" },
  { key: "executiveHire", label: "Executive hire or departure" },
];

const FREQUENCIES: { value: NovaSynthProfile["notificationFrequency"]; label: string }[] = [
  { value: "realtime", label: "Real-time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3.5 py-2.5 bg-[#070d1a] border border-[#1a2640] hover:border-[#263350] rounded-xl text-sm text-slate-100 placeholder-slate-700 focus:outline-none focus:border-brand-600/50 focus:ring-1 focus:ring-brand-600/30 transition-all duration-200";

/**
 * Account settings - edit the onboarding profile (company, tracked competitors,
 * signals, alerts, Slack) after first-run. Saves to the account-tied store via
 * persistProfile so changes follow the account across browsers/devices.
 */
export function Settings({ idToken, profile, onClose, onSaved }: SettingsProps) {
  // Default the alert email to the signed-in account's email so the user never
  // has to re-type it (they can still override). Falls back to any saved value.
  const signupEmail = emailFromIdToken(idToken);
  const [draft, setDraft] = useState<NovaSynthProfile>(() => {
    const base = { ...defaultProfile(), ...(profile ?? {}) };
    if (!base.alertEmail && signupEmail) base.alertEmail = signupEmail;
    return base;
  });
  const [competitorInput, setCompetitorInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  function patch(p: Partial<NovaSynthProfile>) {
    setDraft((prev) => ({ ...prev, ...p }));
    setSavedAt(null);
  }

  function addCompetitor() {
    const t = competitorInput.trim();
    if (!t || draft.competitors.includes(t) || draft.competitors.length >= 5) return;
    patch({ competitors: [...draft.competitors, t] });
    setCompetitorInput("");
  }

  async function save() {
    if (!draft.companyName.trim()) return;
    setSaving(true);
    try {
      await persistProfile(idToken, draft);
      setSavedAt(Date.now());
      onSaved(draft);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm px-4 py-10">
      <div className="relative w-full max-w-2xl bg-[#0d1424] border border-[#1a2640] rounded-2xl shadow-card-lg animate-fade-in">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[#1a2640] bg-[#0d1424]/95 backdrop-blur rounded-t-2xl">
          <div>
            <h2 className="text-base font-semibold text-white">Settings</h2>
            <p className="text-[11px] text-slate-600 mt-0.5">Saved to your account - synced across devices</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-300 hover:bg-[#1a2640] rounded-lg transition-colors cursor-pointer"
            aria-label="Close settings"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Company */}
          <section className="space-y-4">
            <h3 className="text-xs font-bold text-brand-400 uppercase tracking-widest">Company</h3>
            <Field label="Company name">
              <input className={inputCls} value={draft.companyName} onChange={(e) => patch({ companyName: e.target.value })} placeholder="Your company name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Industry">
                <select className={inputCls} value={draft.industry} onChange={(e) => patch({ industry: e.target.value })}>
                  <option value="">Select…</option>
                  {INDUSTRIES.map((o) => <option key={o} value={o} className="bg-[#0d1424]">{o}</option>)}
                </select>
              </Field>
              <Field label="Company size">
                <select className={inputCls} value={draft.companySize} onChange={(e) => patch({ companySize: e.target.value })}>
                  <option value="">Select…</option>
                  {SIZES.map((o) => <option key={o} value={o} className="bg-[#0d1424]">{o}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* Positioning - optional personalization for sharper threat reads */}
          <section className="space-y-4">
            <div>
              <h3 className="text-xs font-bold text-brand-400 uppercase tracking-widest">Positioning</h3>
              <p className="mt-1 text-[11px] text-slate-600">Optional - sharpens how threats are framed relative to your business.</p>
            </div>
            {([
              { key: "icp" as const, label: "Ideal customer profile", placeholder: "e.g. Mid-market B2B SaaS, RevOps-led buying", rows: 1 },
              { key: "targetSegments" as const, label: "Target segments", placeholder: "e.g. Financial services, healthcare, retail", rows: 1 },
              { key: "valueProps" as const, label: "Value propositions", placeholder: "What you lead with - the outcomes you sell", rows: 2 },
              { key: "differentiators" as const, label: "Differentiators", placeholder: "What competitors can't easily match", rows: 2 },
              { key: "winLossNotes" as const, label: "Win / loss notes", placeholder: "Why you win, why you lose", rows: 2 },
            ]).map((f) => (
              <Field key={f.key} label={f.label}>
                <textarea
                  className={`${inputCls} resize-none leading-relaxed`}
                  rows={f.rows}
                  value={draft[f.key] ?? ""}
                  onChange={(e) => patch({ [f.key]: e.target.value } as Partial<NovaSynthProfile>)}
                  placeholder={f.placeholder}
                />
              </Field>
            ))}
          </section>

          {/* Competitors */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold text-brand-400 uppercase tracking-widest">Tracked competitors ({draft.competitors.length}/5)</h3>
            <div className="flex gap-2">
              <input
                className={`${inputCls} flex-1 font-mono`}
                value={competitorInput}
                onChange={(e) => setCompetitorInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompetitor(); } }}
                placeholder="e.g. Snowflake"
                disabled={draft.competitors.length >= 5}
              />
              <button
                onClick={addCompetitor}
                disabled={!competitorInput.trim() || draft.competitors.length >= 5}
                className="px-4 py-2.5 bg-brand-700/30 hover:bg-brand-700/50 border border-brand-600/30 text-brand-300 text-sm font-semibold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {draft.competitors.map((c) => (
                <span key={c} className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-[#070d1a] border border-[#1a2640] rounded-lg text-sm font-mono text-slate-200">
                  {c}
                  <button onClick={() => patch({ competitors: draft.competitors.filter((x) => x !== c) })} className="text-slate-600 hover:text-red-400 cursor-pointer" aria-label={`Remove ${c}`}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
          </section>

          {/* Alerts */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold text-brand-400 uppercase tracking-widest">Alerts</h3>

            {/* Email alerts - re-enabled now that email delivery is live. The
                address defaults to the account's signup email above; this toggle
                turns scheduled email alerts on/off without losing the address. */}
            <label className="flex items-center justify-between gap-3 px-3.5 py-3 bg-[#070d1a] border border-[#1a2640] rounded-xl cursor-pointer">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700/20 border border-brand-600/25 text-brand-300 shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-200">Email me alerts</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Get intelligence updates in your inbox.</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={draft.emailAlerts}
                onChange={(e) => patch({ emailAlerts: e.target.checked })}
                className="accent-brand-500 w-4 h-4 cursor-pointer shrink-0"
              />
            </label>

            <Field label="Alert email">
              <input className={inputCls} type="email" value={draft.alertEmail} onChange={(e) => patch({ alertEmail: e.target.value })} placeholder="your@email.com" disabled={!draft.emailAlerts} />
            </Field>
            <Field label="Notification frequency">
              <div className="grid grid-cols-3 gap-2">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => patch({ notificationFrequency: f.value })}
                    className={`p-2.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${draft.notificationFrequency === f.value ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "border-[#1a2640] bg-[#070d1a] text-slate-300 hover:border-[#263350]"}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </Field>
            <div className="space-y-2">
              {TRIGGERS.map((t) => (
                <label key={t.key} className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-[#070d1a] border border-[#1a2640] rounded-xl cursor-pointer">
                  <span className="text-sm text-slate-200">{t.label}</span>
                  <input type="checkbox" checked={draft.alertTriggers[t.key]} onChange={(e) => patch({ alertTriggers: { ...draft.alertTriggers, [t.key]: e.target.checked } })} className="accent-brand-500 w-4 h-4 cursor-pointer" />
                </label>
              ))}
            </div>
          </section>

          {/* Integrations */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold text-brand-400 uppercase tracking-widest">Integrations</h3>
            {/* Guided Slack connect. onSave persists the whole current draft with
                the new webhook so it sticks immediately (the test button then
                reads it server-side) without requiring the main Save click. */}
            <SlackConnect
              idToken={idToken}
              value={draft.slackWebhook}
              onSave={async (webhook) => {
                const updated = { ...draft, slackWebhook: webhook };
                setDraft(updated);
                await persistProfile(idToken, updated);
                onSaved(updated);
              }}
            />
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-[#1a2640] bg-[#091020]/80 backdrop-blur rounded-b-2xl">
          <span className="text-[11px] text-slate-600">
            {savedAt ? "✓ Saved to your account" : "Changes are not saved until you click Save"}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-[#1a2640] rounded-xl transition-colors cursor-pointer">Cancel</button>
            <button
              onClick={save}
              disabled={saving || !draft.companyName.trim()}
              className="px-5 py-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl transition-all shadow-glow-brand-sm disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
